import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Globe, GraduationCap, Heart, Users } from "lucide-react";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <Button
                variant="ghost"
                className="text-white hover:text-white/80 mb-4"
                onClick={() => navigate("/")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="about-title">
                About Berni
              </h1>
              <p className="text-xl text-white/90">Founder of Le Prof Berni</p>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_languagecoach/artifacts/iatwncw6_Logo%20le%20prof.png" 
              alt="Le Prof Berni Logo" 
              className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg"
              data-testid="about-logo"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Content Card */}
        <Card className="shadow-2xl mb-8" data-testid="about-content">
          <CardContent className="p-8 sm:p-12">
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p className="text-lg">
                Hi, I'm <span className="font-bold text-[#04539F]">Berni</span>, a language teacher, coach, and eternal learner with more than <span className="font-semibold">16 years of experience</span> helping students from over <span className="font-semibold">30 countries</span> discover the joy of speaking French.
              </p>

              <p>
                My teaching journey began at the <span className="font-semibold">Lycée Français de Caracas</span>, where I first learned what it means to truly live a language. Over the years, I continued refining my craft by teaching at institutions such as the <span className="font-semibold">Alliance Française</span> and <span className="font-semibold">Learnlight</span>, as well as working with professionals from global companies like <span className="font-semibold">Siemens Gamesa, Louis Vuitton, Prada, Mango, Inditex, Global Power Generation, DSM-Firmenich</span>, and many others.
              </p>

              <p>
                Through more than <span className="font-bold text-[#04539F]">20,000 hours</span> spent teaching people of all ages and backgrounds, one truth has always been clear to me: <span className="italic">the most powerful way to learn a language is through immersion and context</span>.
              </p>

              <p>
                Not just memorizing rules, but experiencing the language, understanding its rhythm, its culture, and the way real people use it every day.
              </p>

              <p>
                What motivates me most isn't grammar or textbooks (though I love those too). It's the transformation I get to witness. Seeing someone go from eager but unsure to confident, expressive, and genuinely in love with the language is the reason I wake up excited to teach. Nothing brings me more joy than watching a student thrive not only speaking French, but embracing everything that comes with it.
              </p>

              <div className="bg-[#04539F]/10 rounded-xl p-6 my-8">
                <p className="text-lg font-semibold text-[#04539F] mb-3">
                  Today, through Le Prof Berni, my mission is simple:
                </p>
                <p className="text-gray-700 italic">
                  To guide each learner with empathy, clarity, and a method rooted in real-life communication, so they can live the language, not just study it.
                </p>
              </div>

              <p className="text-lg font-medium text-gray-800">
                If you're ready to experience French in a way that feels natural, human, and motivating, I'm here to help you every step of the way.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="shadow-lg" data-testid="stat-card-years">
            <CardContent className="p-6 text-center">
              <GraduationCap className="w-12 h-12 text-[#04539F] mx-auto mb-3" />
              <div className="text-3xl font-bold text-[#04539F]">17+</div>
              <div className="text-sm text-gray-600 mt-1">Years Experience</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg" data-testid="stat-card-countries">
            <CardContent className="p-6 text-center">
              <Globe className="w-12 h-12 text-[#04539F] mx-auto mb-3" />
              <div className="text-3xl font-bold text-[#04539F]">30+</div>
              <div className="text-sm text-gray-600 mt-1">Countries</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg" data-testid="stat-card-hours">
            <CardContent className="p-6 text-center">
              <Heart className="w-12 h-12 text-[#04539F] mx-auto mb-3" />
              <div className="text-3xl font-bold text-[#04539F]">20,000+</div>
              <div className="text-sm text-gray-600 mt-1">Teaching Hours</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg" data-testid="stat-card-students">
            <CardContent className="p-6 text-center">
              <Users className="w-12 h-12 text-[#04539F] mx-auto mb-3" />
              <div className="text-3xl font-bold text-[#04539F]">92+</div>
              <div className="text-sm text-gray-600 mt-1">Happy Students</div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            className="bg-white text-[#04539F] hover:bg-white/90 font-semibold px-8 py-6 text-lg rounded-full transition-all hover:scale-105"
            onClick={() => navigate("/")}
            data-testid="start-learning-btn"
          >
            Start Learning with Berni
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#04539F] text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white/80 text-xs transition-colors"
          >
            <img
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
              alt="Emergent"
              className="w-4 h-4"
            />
            Made with Emergent
          </a>
        </div>
      </footer>
    </div>
  );
};

export default About;
